<template>
  <div
    class="message-bubble d-flex flex-column mb-3"
    :class="isUser ? 'align-end' : 'align-start'"
  >
    <v-card
      :color="isUser ? 'primary' : 'surface-variant'"
      :class="isUser ? 'text-on-primary' : 'agent-bubble'"
      rounded="lg"
      class="pa-3"
      :style="{ maxWidth: isUser ? '85%' : '95%' }"
      :elevation="isUser ? 1 : 0"
    >
      <AssistantContent
        v-if="!isUser"
        :content="message.content"
        :streaming="!!message._streaming"
      />
      <!-- User message with image attachments (array content) -->
      <div v-else-if="Array.isArray(message.content)" class="user-content">
        <div
          v-for="(part, i) in message.content"
          :key="i"
        >
          <div v-if="part.type === 'text'" class="text-body-2" style="white-space: pre-wrap;">{{ partText(part) }}</div>
          <v-img
            v-else-if="part.type === 'image_url'"
            :src="part.image_url.url"
            max-width="300"
            class="rounded mt-1"
          />
        </div>
        <div v-if="userAttachments.length" class="d-flex ga-1 flex-wrap mt-1">
          <v-chip
            v-for="(att, i) in userAttachments"
            :key="i"
            size="small"
            variant="tonal"
            label
            class="text-none"
            prepend-icon="mdi-file-table-outline"
          >
            {{ att.fileName }}
            <span v-if="att.size" class="ml-1 text-medium-emphasis">{{ att.size }}</span>
          </v-chip>
        </div>
      </div>
      <!-- Plain text user message -->
      <div v-else>
        <div v-if="userText" class="text-body-2" style="white-space: pre-wrap;">{{ userText }}</div>
        <div v-if="userAttachments.length" class="d-flex ga-1 flex-wrap" :class="{ 'mt-1': userText }">
          <v-chip
            v-for="(att, i) in userAttachments"
            :key="i"
            size="small"
            variant="tonal"
            label
            class="text-none"
            prepend-icon="mdi-file-table-outline"
          >
            {{ att.fileName }}
            <span v-if="att.size" class="ml-1 text-medium-emphasis">{{ att.size }}</span>
          </v-chip>
        </div>
      </div>

      <!-- Server-emitted links (download and/or navigation chips — a turn can carry several) -->
      <div v-if="messageLinks.length" class="mt-3">
        <div
          v-for="(link, i) in messageLinks"
          :key="link.url"
          :class="{ 'mt-2': i > 0 }"
        >
          <div v-if="link.description" class="text-body-2 mb-2">
            {{ link.description }}
          </div>
          <div class="d-inline-flex align-center ga-1">
            <v-chip
              :color="styleFor(link).color"
              variant="tonal"
              label
              :prepend-icon="downloadingUrl === link.url ? 'mdi-loading' : styleFor(link).icon"
              :class="['text-none font-weight-medium', { 'chip-loading': downloadingUrl === link.url }]"
              :disabled="downloadingUrl === link.url"
              @click="onLinkClick(link)"
            >
              {{ link.displayName || (isDownload(link) ? t('chat.download') : '') }}
            </v-chip>
            <!-- Downloads need an authenticated fetch, so a plain new-tab open would
                 fail (no headers) — hide the button and rely on the chip. -->
            <v-btn
              v-if="!isDownload(link)"
              icon
              size="x-small"
              variant="text"
              :title="t('chat.openInNewTab')"
              @click="openLinkInNewTab(link)"
            >
              <v-icon size="14">mdi-open-in-new</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Streaming indicator -->
      <span
        v-if="message._streaming"
        class="streaming-cursor"
      >|</span>
    </v-card>
  </div>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { computed, ref } from 'vue'
import { linkStyleMap, defaultLinkStyle } from '../../utils/linkStyles.js'
import { useParentBridge } from '../../composables/useParentBridge.js'
import { parseFileStubs } from '../../stores/chat.js'
import { downloadFile } from '../../api/files.js'
import AssistantContent from './AssistantContent.vue'

const props = defineProps({
  message: { type: Object, required: true },
})

const emit = defineEmits(['link-click'])

const isUser = computed(() => props.message.role === 'user')
const bridge = useParentBridge()
// URL of the link currently being fetched for download (null when idle) —
// per-link so one chip's spinner doesn't light up its siblings.
const downloadingUrl = ref(null)

// User bubble display: split the appended upload stub(s) out of the message text
// so the raw stub never shows — the text renders clean and each attachment shows
// as a chip. Kept display-only; the stub stays in what was SENT to the backend.
const userParsed = computed(() =>
  isUser.value && typeof props.message.content === 'string'
    ? parseFileStubs(props.message.content)
    : { text: '', attachments: [] }
)
const userText = computed(() => userParsed.value.text)
const userAttachments = computed(() => {
  if (!isUser.value) return []
  if (typeof props.message.content === 'string') return userParsed.value.attachments
  if (Array.isArray(props.message.content)) {
    return props.message.content.flatMap((part) =>
      part.type === 'text' ? parseFileStubs(part.text).attachments : []
    )
  }
  return []
})
function partText(part) {
  return parseFileStubs(part.text).text
}

// All server-emitted links on this bubble. `_links` is the current shape;
// `_link` is tolerated for any message object predating the list migration.
const messageLinks = computed(() =>
  props.message._links || (props.message._link ? [props.message._link] : [])
)

function styleFor(link) {
  return (link.viewType && linkStyleMap[link.viewType]) || defaultLinkStyle
}

function isDownload(link) {
  return link.viewType === 'file_download'
}

function openLinkInNewTab(link) {
  if (!link) return
  window.open(link.url, '_blank', 'noopener,noreferrer')
}

async function onLinkClick(link) {
  if (!link) return
  // File downloads sit behind the same auth as the rest of the API, so fetch the
  // bytes with the client's headers and save via a blob object URL — a plain
  // navigation/new-tab would drop the auth headers.
  if (isDownload(link)) {
    if (downloadingUrl.value) return
    downloadingUrl.value = link.url
    try {
      await downloadFile(link.url, link.displayName)
    } catch (e) {
      // Non-fatal: surface via the same link-click channel hosts already listen on.
      emit('link-click', { url: link.url, viewType: link.viewType, displayName: link.displayName, error: e })
    } finally {
      downloadingUrl.value = null
    }
    return
  }
  // Give the host a chance to intercept (e.g. route via Vue Router) and only
  // fall through to bridge/new-tab if no handler called preventDefault.
  const intent = { url: link.url, viewType: link.viewType, displayName: link.displayName, defaultPrevented: false, preventDefault() { this.defaultPrevented = true } }
  emit('link-click', intent)
  if (intent.defaultPrevented) return

  if (bridge.isActive) {
    // Same-origin links route into the host SPA's router; external links and
    // bridge failures fall back to a new tab (all handled inside navigate()).
    bridge.navigate(link.url)
    return
  }

  openLinkInNewTab()
}
</script>

<style scoped>
.message-bubble.align-start :deep(.markdown-content) {
  font-size: 0.875rem;
}
/* In light mode, render the agent bubble as solid white against the
   #fafafa chat background instead of the theme's grey surface-variant. */
.v-theme--light .agent-bubble {
  background-color: #fff !important;
}
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
.streaming-cursor {
  animation: blink 1s step-end infinite;
  font-weight: bold;
}
/* Chips are white-space: nowrap, so a long filename (attachment or download
   displayName) is unbreakably wide. Cap the chip rows at the bubble width and
   let the chips shrink so v-chip's own overflow handling truncates the label
   instead of blowing the bubble (and the whole chat body) past the viewport. */
.message-bubble .v-card .d-inline-flex {
  max-width: 100%;
}
.message-bubble .v-chip {
  min-width: 0;
}
/* Spin the loading icon while an authenticated download is in flight. */
.chip-loading :deep(.v-chip__prepend .v-icon) {
  animation: chip-spin 0.8s linear infinite;
}
@keyframes chip-spin {
  to { transform: rotate(360deg); }
}
</style>
